/**
 * FACE ID / TOUCH ID FOR THE VAULT — owner, 2026-10-01: 在 Apple 的 device 上面, 有沒有辦法讓
 * Vault 使用 Touch ID 或是 Face ID. Chosen: option 1, a passkey. Probe passed on his devices
 * 2026-10-02 (iPhone Safari + Mac): Face ID / Touch ID releases the passkey's PRF secret, the
 * same secret every time, and the passkey syncs through iCloud Keychain.
 *
 * The PRF secret wraps the vault key (vaultCrypto passkeyWrap) — a third door beside the
 * password and recovery code. The server keeps only the wrap and the credential id.
 * WebAuthn + WebCrypto only: no crypto code of ours ships (export compliance).
 *
 * IN THE iOS APP only from a shell that declares the sites' passkeys (Associated Domains,
 * `webcredentials:` — App.entitlements) and says so with `AvengentsPasskeys/1` in its
 * User-Agent; the site serves the matching apple-app-site-association. An older shell shows
 * no Face ID door and keeps what it has (Apple Passwords filling the password behind Face ID).
 */
import type { StoredVault } from '../transport/relay'
import { passkeyWrap, unlockWithPasskeyPrf, type PasskeyWrap } from './vaultCrypto'

type Bytes = Uint8Array<ArrayBuffer>

/** The fixed PRF input; the per-door HKDF salt makes each wrap its own key. */
const PRF_INPUT = new Uint8Array(new TextEncoder().encode('avengents-vault-v1:prf'))
const NAME = 'Avengents backup vault'

const b64url = (b: ArrayBuffer | Uint8Array) => {
  const a = b instanceof Uint8Array ? b : new Uint8Array(b)
  let s = ''
  for (let i = 0; i < a.length; i++) s += String.fromCharCode(a[i]!)
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}
const unb64url = (s: string): Bytes => {
  const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/'))
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}
const rand = (n: number): Bytes => globalThis.crypto.getRandomValues(new Uint8Array(n))

/**
 * Inside one of our apps, but a build that does NOT declare the sites' passkeys: App Store
 * iOS 1.2 and Mac 0.3 and older. Every shell stamps `AvengentsShell/` into its User-Agent
 * (the iOS shell through Capacitor, the Mac shell — AppKit + WKWebView, not Capacitor —
 * itself); one that carries the entitlement adds `AvengentsPasskeys/1`.
 */
function shellWithoutPasskeys(): boolean {
  const ua = globalThis.navigator?.userAgent ?? ''
  const cap = (globalThis as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor
  const inShell = !!cap?.isNativePlatform?.() || /\bAvengentsShell\//.test(ua)
  return inShell && !/\bAvengentsPasskeys\/\d+/.test(ua)
}

let available: Promise<boolean> | null = null
/** Can this device show the Face ID / Touch ID door at all. */
export function faceIdAvailable(): Promise<boolean> {
  available ??= (async () => {
    if (shellWithoutPasskeys()) return false
    const PKC = (globalThis as { PublicKeyCredential?: { isUserVerifyingPlatformAuthenticatorAvailable?: () => Promise<boolean> } })
      .PublicKeyCredential
    if (!PKC?.isUserVerifyingPlatformAuthenticatorAvailable) return false
    return PKC.isUserVerifyingPlatformAuthenticatorAvailable().catch(() => false)
  })()
  return available
}

/** Tests only. */
export function resetFaceIdAvailable() {
  available = null
}

/** What the person reads when Face ID did not give the vault its secret. */
export class FaceIdFailed extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'FaceIdFailed'
  }
}

function why(e: unknown): FaceIdFailed {
  const name = e instanceof Error ? e.name : ''
  if (name === 'NotAllowedError' || name === 'AbortError') return new FaceIdFailed('Face ID / Touch ID was cancelled.')
  if (name === 'InvalidStateError') return new FaceIdFailed('This device already has a vault passkey.')
  return new FaceIdFailed(`Face ID / Touch ID did not work here: ${e instanceof Error ? e.message : String(e)}`)
}

type PrfResults = { prf?: { enabled?: boolean; results?: { first?: ArrayBuffer } } }

async function assertion(creds: string[]): Promise<{ cred: string; prf: Bytes }> {
  let got: PublicKeyCredential | null
  try {
    got = (await navigator.credentials.get({
      publicKey: {
        rpId: location.hostname,
        challenge: rand(32),
        userVerification: 'required',
        allowCredentials: creds.map((c) => ({ type: 'public-key' as const, id: unb64url(c) })),
        extensions: { prf: { eval: { first: PRF_INPUT } } } as AuthenticationExtensionsClientInputs,
      },
    })) as PublicKeyCredential | null
  } catch (e) {
    throw why(e)
  }
  if (!got) throw new FaceIdFailed('Face ID / Touch ID was cancelled.')
  const first = (got.getClientExtensionResults() as PrfResults).prf?.results?.first
  if (!first) throw new FaceIdFailed('This passkey cannot unlock a vault on this device.')
  return { cred: b64url(got.rawId), prf: new Uint8Array(first) }
}

/** The vault key, behind Face ID / Touch ID. Throws FaceIdFailed or WrongSecret. */
export async function unlockWithFaceId(vault: StoredVault): Promise<Bytes> {
  const creds = (vault.pk ?? []).map((w) => w.cred)
  if (!creds.length) throw new FaceIdFailed('Face ID / Touch ID is not set up for this vault.')
  const { cred, prf } = await assertion(creds)
  try {
    return await unlockWithPasskeyPrf(vault, cred, prf)
  } finally {
    prf.fill(0)
  }
}

/**
 * A new passkey for this vault and the vault key wrapped under it. `vk` is the vault key the
 * person just unlocked with the password. One passkey per vault per account: the user id is
 * the vault's public key, so making it again on a synced device REPLACES the old one rather
 * than piling up entries in Apple Passwords.
 */
export async function makeVaultPasskey(vault: StoredVault, vk: Bytes): Promise<PasskeyWrap> {
  const userId = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(vault.vpk))).slice(0, 16)
  let made: PublicKeyCredential | null
  try {
    made = (await navigator.credentials.create({
      publicKey: {
        rp: { name: 'Avengents', id: location.hostname },
        user: { id: userId, name: NAME, displayName: NAME },
        challenge: rand(32),
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 },
          { type: 'public-key', alg: -257 },
        ],
        authenticatorSelection: { residentKey: 'required', userVerification: 'required' },
        extensions: { prf: { eval: { first: PRF_INPUT } } } as AuthenticationExtensionsClientInputs,
      },
    })) as PublicKeyCredential | null
  } catch (e) {
    throw why(e)
  }
  if (!made) throw new FaceIdFailed('Face ID / Touch ID was cancelled.')
  const ext = made.getClientExtensionResults() as PrfResults
  if (ext.prf?.enabled === false) throw new FaceIdFailed('This device cannot use a passkey to unlock a vault.')
  const cred = b64url(made.rawId)
  // Some platforms return the secret at creation; the rest need one unlock to give it.
  const first = ext.prf?.results?.first
  const prf = first ? new Uint8Array(first) : (await assertion([cred])).prf
  try {
    return { ...(await passkeyWrap(vk, prf, cred)), at: Math.floor(Date.now() / 1000) }
  } finally {
    prf.fill(0)
  }
}
